import mixpanel from 'mixpanel-browser'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useOnMount } from './useOnMount'
import type { Config, RequestOptions } from 'mixpanel-browser'
import type { ReactNode } from 'react'

type Properties = Record<string, any>
type Event = string

interface MixpanelContextValues {
  getDistinctId: () => string
  identify: (id?: string) => void
  init: (config: Partial<Config>, callBack: (loaded: boolean) => void) => void
  isLoaded: boolean
  track: (
    event: Event,
    properties?: Properties,
    options?: RequestOptions,
  ) => void
  trackLinks: (selector: string, event: Event, properties?: Properties) => void
  register: (properties: Properties) => void
  registerOnce: (properties: Properties) => void
  reset: () => void
}

const DEFAULT_VALUES: MixpanelContextValues = {
  getDistinctId: () => '',
  identify: () => {},
  init: () => {},
  isLoaded: false,
  track: () => {},
  trackLinks: () => {},
  register: () => {},
  registerOnce: () => {},
  reset: () => {},
}

interface MixpanelProviderProps {
  children: ReactNode
  token?: string
  apiHost?: string
  config?: Partial<Config>
  preInit?: (() => Promise<boolean>) | null
}

const Context = createContext<MixpanelContextValues>(DEFAULT_VALUES)

export function MixpanelProvider({
  children,
  token = undefined,
  apiHost = undefined,
  config = undefined,
  preInit = null,
}: MixpanelProviderProps) {
  const [isMixpanelLoaded, setMixpanelLoaded] = useState<boolean>(false)
  const [isCaptaincauseOffice, setIsCaptaincauseOffice] = useState(false)

  const track = useCallback(
    (
      event: Event,
      properties: Properties = {},
      options: RequestOptions = {},
    ) => {
      if (isMixpanelLoaded) {
        try {
          mixpanel.track(event, properties, options)
        } catch (error: unknown) {
          /* empty */
        }
      }
    },
    [isMixpanelLoaded],
  )

  const identify = useCallback(
    (id?: string) => {
      if (isMixpanelLoaded) {
        try {
          mixpanel.identify(id)
        } catch (error: unknown) {
          /* empty */
        }
      }
    },
    [isMixpanelLoaded],
  )

  const getDistinctId = useCallback(() => {
    if (isMixpanelLoaded) {
      try {
        return mixpanel.get_distinct_id()
      } catch (error: unknown) {
        return ''
      }
    }

    return ''
  }, [isMixpanelLoaded])

  const reset = useCallback(() => {
    if (isMixpanelLoaded) {
      try {
        mixpanel.reset()
      } catch (error: unknown) {
        /* empty */
      }
    }
  }, [isMixpanelLoaded])

  const trackLinks = useCallback(
    (selector: string, event: Event, properties?: Properties) => {
      if (isMixpanelLoaded) {
        try {
          mixpanel.track_links(selector, event, properties)
        } catch (error: unknown) {
          /* empty */
        }
      }
    },
    [isMixpanelLoaded],
  )

  const register = useCallback(
    (properties: Properties) => {
      if (isMixpanelLoaded) {
        try {
          mixpanel.register({
            ...properties,
            is_captaincause_office: isCaptaincauseOffice,
          })
        } catch (error: unknown) {
          /* empty */
        }
      }
    },
    [isMixpanelLoaded, isCaptaincauseOffice],
  )

  const registerOnce = useCallback(
    (properties: Properties) => {
      if (isMixpanelLoaded) {
        try {
          mixpanel.register_once(properties)
        } catch (error: unknown) {
          /* empty */
        }
      }
    },
    [isMixpanelLoaded],
  )

  const init = useCallback(async () => {
    if (token) {
      mixpanel.init(token, {
        api_host: apiHost,
        debug: process.env.NODE_ENV === 'development',
        ignore_dnt: true,
        disable_persistence: true,
        loaded: loadedMixpanel => {
          setMixpanelLoaded(!!loadedMixpanel)
        },
        ...config,
      })
    }
  }, [token, apiHost, config])

  useOnMount(() => {
    async function check() {
      if (preInit) {
        const result = await preInit()
        setIsCaptaincauseOffice(result)
      }
    }
    check()
    init()
  })

  const values: MixpanelContextValues = useMemo(
    () => ({
      getDistinctId,
      identify,
      init,
      isLoaded: isMixpanelLoaded,
      track,
      trackLinks,
      register,
      registerOnce,
      reset,
    }),
    [
      getDistinctId,
      identify,
      init,
      register,
      track,
      trackLinks,
      registerOnce,
      isMixpanelLoaded,
      reset,
    ],
  )

  return <Context.Provider value={values}>{children}</Context.Provider>
}

export function useMixpanel() {
  return useContext(Context)
}

export function useRawMixpanel() {
  return mixpanel
}
